import type { WidgetConfig } from '@/types/config';

/**
 * Iframe component - manages iframe creation and lifecycle
 */
export class Iframe {
  private iframe: HTMLIFrameElement | null = null;
  private loadTimeout: number | null = null;
  private positionInterval: number | null = null;

  constructor(
    private url: string,
    private config: WidgetConfig,
    private containerId: string
  ) {}

  /**
   * Create iframe element
   */
  public create(): HTMLIFrameElement {
    if (this.iframe) {
      console.warn('[CaredoveWidget] Iframe already exists');
      return this.iframe;
    }

    const iframe = document.createElement('iframe');
    iframe.id = this.containerId;
    iframe.src = this.url;
    iframe.className = 'caredove-iframe';
    iframe.setAttribute('allow', 'clipboard-write; clipboard-read');
    iframe.setAttribute('loading', 'lazy');

    // Set dimensions based on mode
    if (this.config.mode === 'overlay') {
      iframe.style.width = '100%';
      iframe.style.height = this.config.height || 'calc(100vh - 50px)';
      iframe.style.minHeight = `${this.config.minHeight}px`;
    } else if (this.config.mode === 'inline' || this.config.mode === 'inline-simple') {
      iframe.style.width = '100%';
      if (this.config.height) {
        iframe.style.height = this.config.height;
      } else {
        iframe.style.minHeight = `${this.config.minHeight}px`;
      }
    }

    iframe.style.border = 'none';

    this.iframe = iframe;
    return iframe;
  }

  /**
   * Get the iframe element
   */
  public getElement(): HTMLIFrameElement | null {
    return this.iframe;
  }

  /**
   * Wait for iframe to load
   */
  public onLoad(callback: () => void, timeout = 10000): void {
    if (!this.iframe) {
      console.error('[CaredoveWidget] Cannot wait for load: iframe does not exist');
      return;
    }

    const handleLoad = (): void => {
      if (this.loadTimeout) {
        clearTimeout(this.loadTimeout);
        this.loadTimeout = null;
      }
      callback();
    };

    this.iframe.addEventListener('load', handleLoad, { once: true });

    // Set timeout for slow loads
    this.loadTimeout = window.setTimeout(() => {
      console.warn('[CaredoveWidget] Iframe load timeout');
      callback(); // Call anyway to hide spinner
    }, timeout);
  }

  /**
   * Update iframe height
   */
  public updateHeight(height: number): void {
    if (!this.iframe) return;

    if (this.config.variableHeight) {
      this.iframe.style.height = `${height}px`;
    }
  }

  /**
   * Update iframe width
   */
  public updateWidth(width: string): void {
    if (!this.iframe) return;

    if (this.config.variableWidth) {
      this.iframe.style.width = width;
      this.iframe.style.minWidth = width;
      this.iframe.style.maxWidth = width;
    }
  }

  /**
   * Get iframe position on page
   */
  public getPosition(): { x: number; y: number } {
    if (!this.iframe) {
      return { x: 0, y: 0 };
    }

    let element: HTMLElement | null = this.iframe;
    let xPos = 0;
    let yPos = 0;

    while (element) {
      if (element.tagName === 'BODY') {
        const xScroll = element.scrollLeft || document.documentElement.scrollLeft;
        const yScroll = element.scrollTop || document.documentElement.scrollTop;

        xPos += element.offsetLeft - xScroll + element.clientLeft;
        yPos += element.offsetTop - yScroll + element.clientTop;
      } else {
        xPos += element.offsetLeft - element.scrollLeft + element.clientLeft;
        yPos += element.offsetTop - element.scrollTop + element.clientTop;
      }

      element = element.offsetParent as HTMLElement | null;
    }

    return { x: xPos, y: yPos };
  }

  /**
   * Start tracking position and sending updates to iframe
   */
  public startPositionTracking(sendMessage: (position: number) => void): void {
    const updatePosition = (): void => {
      const position = this.getPosition();
      if (Math.abs(position.y) > 0) {
        sendMessage(Math.abs(position.y));
      }
    };

    // Initial update
    updatePosition();

    // Listen to scroll events
    window.addEventListener('scroll', updatePosition);

    // Cleanup function
    this.positionInterval = window.setInterval(updatePosition, 1000);
  }

  /**
   * Stop position tracking
   */
  public stopPositionTracking(): void {
    if (this.positionInterval) {
      clearInterval(this.positionInterval);
      this.positionInterval = null;
    }
  }

  /**
   * Focus the iframe
   */
  public focus(): void {
    if (this.iframe?.contentWindow) {
      this.iframe.contentWindow.focus();
    }
  }

  /**
   * Scroll to iframe position
   */
  public scrollIntoView(offset = 200): void {
    if (!this.iframe) return;

    const position = this.getPosition();
    window.scrollTo({
      top: position.y - offset,
      behavior: 'smooth',
    });
  }

  /**
   * Destroy iframe and cleanup
   */
  public destroy(): void {
    this.stopPositionTracking();

    if (this.loadTimeout) {
      clearTimeout(this.loadTimeout);
      this.loadTimeout = null;
    }

    if (this.iframe) {
      this.iframe.remove();
      this.iframe = null;
    }
  }
}
